<template>
  <div class="upload-wrapper">
    <el-upload
      class="upload-container"
      :action="action"
      :headers="headers"
      :data="data"
      :multiple="multiple"
      :limit="limit"
      :http-request="uploadRequest"
      :on-exceed="handleExceed"
      :on-success="handleSuccess"
      :on-error="handleError"
      :on-progress="handleProgress"
      :on-remove="handleRemove"
      :on-preview="handlePreview"
      :file-list="fileList"
      :list-type="listType"
      :auto-upload="autoUpload"
      :before-upload="beforeUpload"
      :disabled="disabled"
      ref="upload"
    >
      <el-button size="small" type="primary" :disabled="disabled">
        {{ uploadButtonText }}
      </el-button>
      <div slot="tip" class="el-upload__tip" v-if="showTip">
        {{ tipText }}
      </div>
    </el-upload>

    <!-- Progress Dialog -->
    <!-- <el-dialog
      title="Upload Progress"
      :visible.sync="progressDialogVisible"
      width="400px"
      :before-close="handleProgressClose"
    >
      <div v-for="(file, index) in uploadingFiles" :key="index" class="progress-item">
        <div class="file-info">
          <span class="file-name">{{ file.name }}</span>
          <span class="file-size">{{ formatFileSize(file.size) }}</span>
        </div>
        <el-progress 
          :percentage="file.percentage" 
          :status="file.status === 'success' ? 'success' : file.status === 'fail' ? 'exception' : ''"
        ></el-progress>
      </div>
    </el-dialog> -->

    <!-- Image Preview Dialog -->
    <el-dialog :visible.sync="previewDialogVisible" width="50%">
      <img width="100%" :src="previewImageUrl" alt="Preview Image">
    </el-dialog>
  </div>
</template>

<script>
import oss from "../../assets/js/ali-oss";
export default {
  name: 'ImageUpload',
  props: {
    // Upload URL
    action: {
      type: String,
      required: true
    },
    // Request headers
    headers: {
      type: Object,
      default: () => ({})
    },
    // Additional form data
    data: {
      type: Object,
      default: () => ({})
    },
    // Initial file list for preview
    value: {
      type: Array,
      default: () => []
    },
    // Multiple files upload
    multiple: {
      type: Boolean,
      default: false
    },
    // Max number of files
    limit: {
      type: Number,
      default: 1
    },
    // List display type
    listType: {
      type: String,
      default: 'picture'
    },
    // Auto upload after selecting
    autoUpload: {
      type: Boolean,
      default: true
    },
    // Accept file types
    accept: {
      type: String,
      default: 'image/*'
    },
    // Max file size (MB)
    maxSize: {
      type: Number,
      default: 5
    },
    // Disabled state
    disabled: {
      type: Boolean,
      default: false
    },
    // Button text
    uploadButtonText: {
      type: String,
      default: 'Click to upload'
    },
    // Tip text
    tipText: {
      type: String,
      default: 'Only images are allowed, size no more than 5MB'
    },
    // Show tip
    showTip: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      fileList: [],
      uploadingFiles: [],
      progressDialogVisible: false,
      previewDialogVisible: false,
      previewImageUrl: '',
      tempFileList: [] // Temporary storage for handling uploads
    };
  },
  watch: {
    value: {
      handler(newVal) {
        if (newVal && Array.isArray(newVal)) {
          this.fileList = newVal.map(item => {
            // Ensure the item has required properties
            if (typeof item === 'string') {
              return { url: item, name: this.getFileNameFromUrl(item) };
            }
            return item;
          });
        }
      },
      immediate: true
    }
  },
  methods: {
    uploadRequest(option) {
      oss.ossUploadFile(option);
    },
    /**
     * Handle file upload success
     */
    handleSuccess(response, file, fileList) {
      // Update file status
      const uploadingFile = this.uploadingFiles.find(f => f.uid === file.uid);
      if (uploadingFile) {
        uploadingFile.status = 'success';
        uploadingFile.percentage = 100;
      }

      // Update parent component
      this.$emit('input', fileList);
      this.$emit('success', response, file, fileList);

      // Check if all uploads completed
      this.checkAllUploadsCompleted();
    },

    /**
     * Handle file upload error
     */
    handleError(err, file, fileList) {
      // Update file status
      const uploadingFile = this.uploadingFiles.find(f => f.uid === file.uid);
      if (uploadingFile) {
        uploadingFile.status = 'fail';
      }

      this.$emit('error', err, file, fileList);
      this.$message.error('Upload failed');

      // Check if all uploads completed
      this.checkAllUploadsCompleted();
    },

    /**
     * Handle upload progress
     */
    handleProgress(event, file, fileList) {
      // Add file to uploading list if not exists
      let uploadingFile = this.uploadingFiles.find(f => f.uid === file.uid);
      if (!uploadingFile) {
        uploadingFile = {
          uid: file.uid,
          name: file.name,
          size: file.size,
          percentage: 0,
          status: 'uploading'
        };
        this.uploadingFiles.push(uploadingFile);
      }

      // Update progress
      uploadingFile.percentage = Math.floor(event.percent);
      
      // Show progress dialog
      if (!this.progressDialogVisible) {
        this.progressDialogVisible = true;
      }

      this.$emit('progress', event, file, fileList);
    },

    /**
     * Handle file removal
     */
    handleRemove(file, fileList) {
      // Remove from uploading files if exists
      const index = this.uploadingFiles.findIndex(f => f.uid === file.uid);
      if (index > -1) {
        this.uploadingFiles.splice(index, 1);
      }

      this.$emit('input', fileList);
      this.$emit('remove', file, fileList);
    },

    /**
     * Handle file preview
     */
    handlePreview(file) {
      if (file.url) {
        this.previewImageUrl = file.url;
        this.previewDialogVisible = true;
      } else if (file.raw) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.previewImageUrl = e.target.result;
          this.previewDialogVisible = true;
        };
        reader.readAsDataURL(file.raw);
      }
    },

    /**
     * Handle exceeding file limit
     */
    handleExceed(files, fileList) {
      this.$message.warning(`You can only upload ${this.limit} files at most`);
    },

    /**
     * Before upload validation
     */
    beforeUpload(file) {
      // Validate file type
      if (this.accept && !this.validateFileType(file)) {
        this.$message.error('Invalid file type');
        return false;
      }

      // Validate file size
      const maxSizeInBytes = this.maxSize * 1024 * 1024;
      if (file.size > maxSizeInBytes) {
        this.$message.error(`File size cannot exceed ${this.maxSize}MB`);
        return false;
      }

      this.$emit('before-upload', file);
      return true;
    },

    /**
     * Validate file type
     */
    validateFileType(file) {
      if (this.accept === 'image/*') {
        return file.type.startsWith('image/');
      }
      
      const acceptedTypes = this.accept.split(',').map(type => type.trim());
      return acceptedTypes.some(type => {
        if (type.endsWith('/*')) {
          const baseType = type.slice(0, -1); // Remove '*'
          return file.type.startsWith(baseType);
        }
        return file.type === type || file.name.toLowerCase().endsWith(type.toLowerCase());
      });
    },

    /**
     * Check if all uploads completed
     */
    checkAllUploadsCompleted() {
      const unfinished = this.uploadingFiles.some(
        file => file.status !== 'success' && file.status !== 'fail'
      );
      
      if (!unfinished && this.uploadingFiles.length > 0) {
        // All uploads completed, close dialog after delay
        setTimeout(() => {
          this.progressDialogVisible = false;
          this.uploadingFiles = [];
        }, 1000);
      }
    },

    /**
     * Handle progress dialog close
     */
    handleProgressClose() {
      this.progressDialogVisible = false;
    },

    /**
     * Format file size
     */
    formatFileSize(size) {
      if (size < 1024) {
        return size + ' B';
      } else if (size < 1024 * 1024) {
        return (size / 1024).toFixed(2) + ' KB';
      } else {
        return (size / (1024 * 1024)).toFixed(2) + ' MB';
      }
    },

    /**
     * Get file name from URL
     */
    getFileNameFromUrl(url) {
      const parts = url.split('/');
      return parts[parts.length - 1];
    },

    /**
     * Manually trigger upload
     */
    submitUpload() {
      this.$refs.upload.submit();
    },

    /**
     * Clear uploaded files
     */
    clearFiles() {
      this.$refs.upload.clearFiles();
      this.fileList = [];
      this.uploadingFiles = [];
      this.$emit('input', []);
    }
  }
};
</script>

<style scoped>
.upload-wrapper {
  width: 100%;
}

.progress-item {
  margin-bottom: 20px;
}

.progress-item:last-child {
  margin-bottom: 0;
}

.file-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
}

.file-name {
  max-width: 70%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-size {
  color: #999;
}
</style>